import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Device, DeviceId, DeviceType} from '../../../shared/models/device';
import {DevicesService} from '../../../core/services/devices.service';
import {TilesService} from '../../../core/services/tiles.service';
import {MetadataService} from '../../../core/services/metadata.service';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {BulkModalService} from '@features/tile-bulk-operations/shared/services/bulk-modal.service';
import {FeatureFlag, FeatureFlagsService} from '../../../core/services/feature-flags.service';
import {map} from 'rxjs/operators';
import {isMobileDevice} from '../../../shared/utils/device-utils';
import {
  TILE_TYPE_SIMPLE_ANALOG,
  TILE_TYPE_SIMPLE_BOOL,
  TILE_TYPE_SIMPLE_MULTISTATE,
  TileFormat,
  TileId,
  TileType,
} from '../../../shared/models/tile';
import {Option, OptionGroup, OptionGroupMultiSelect} from '../../../shared/models/option';
import {SortTable, SortTableRow} from '../../../shared/models/sort-table/types';
import {BehaviorSubject, Subscription} from 'rxjs';
import {
  getFriendlyEndiannessString,
  getFriendlyTypeString,
  getReadOnlyFunctionsForDataType,
  getReadWriteFunctionsForDataType,
  is1BitDataType,
  makeFriendlyFunctionsString,
  makeTileFromModbusPoint,
  ModbusDataType,
  ModbusPoint,
  ModbusPointID,
} from '../../../shared/models/modbus';
import {
  SortTableCellButtons,
  SortTableCellKebabMenu,
  SortTableCellTextPill,
  SortTableCellTitleDescription,
} from '../../../shared/models/sort-table/cell-classes';
import {Store} from '@ngrx/store';
import {AppState} from '../../../app/app.state';
import {ModbusPointSelectionCallback, PointValue, tileFormatToTileTypeMap} from '../../../shared/models/point';
import {DrawerGapFromTopComponent} from '../drawer-gap-from-top/drawer-gap-from-top.component';
import {PillComponent} from '../pill/pill.component';
import {PopoverMultiSelectComponent} from '../popover-multi-select/popover-multi-select.component';
import {FormsModule} from '@angular/forms';
import {SortTableComponent} from '../sort-table/sort-table.component';
import {ModbusSinglePointComponent} from './single-point/modbus-single-point.component';
import {TileEditorComponent} from '../tile-editor/tile-editor.component';
import {DialogBoxButtonColor} from 'src/shared/models/dialog-box';
import {EMPTY_ID} from 'src/shared/constants/empty-id';
import {MenuButtonComponent} from '../menu-button/menu-button.component';

const COL_IDX_KEBAB = 0;
const COL_IDX_NAME = 1;
const COL_IDX_BUTTON = 2;
const COL_IDX_ADDR = 3;
const COL_IDX_TYPE = 4;
const COL_IDX_BYTE_ORDER = 5;
const COL_IDX_FUNCS = 6;

@Component({
  selector: 'motn-modbus-point-editor',
  templateUrl: './modbus-point-editor.component.html',
  styleUrls: ['./modbus-point-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DrawerGapFromTopComponent,
    PopoverMultiSelectComponent,
    FormsModule,
    SortTableComponent,
    ModbusSinglePointComponent,
    TileEditorComponent,
    MenuButtonComponent,
    PillComponent,
  ],
})
export class ModbusPointEditorComponent implements OnInit, OnDestroy {
  @Input() preselectedDevice: DeviceId;
  @Input() useCase: string = '';
  @Input() formatRestriction: TileFormat;
  /**
   * If customCallback is defined, the default tile creation methods in this class
   * won't be used when the user selects a point from which to create a new tile.
   */
  @Input() customCallback: ModbusPointSelectionCallback;
  /** When true, hides config/edit controls and replaces "Make Tile" with a direct "Select" action. */
  @Input() selectionMode = false;


  @Output() clickedOutside$: EventEmitter<null> = new EventEmitter<null>();

  devices: Device<DeviceType.Modbus>[] = [];
  selectedDevice: DeviceId;
  filteredPoints: ModbusPoint[];
  points: ModbusPoint[];
  searchTerm: string = '';
  editingPoint: ModbusPoint;
  editingTile: TileId = EMPTY_ID;

  reading: boolean = false;
  writing: boolean = false;

  private devicesSub: Subscription;

  deviceMenuOptions: OptionGroupMultiSelect;

  table$: BehaviorSubject<SortTable> = new BehaviorSubject<SortTable>(null);


  constructor(
    private devicesService: DevicesService,
    private tilesService: TilesService,
    private metadataService: MetadataService,
    private shadeTools: ShadeToolsService,
    private store: Store<AppState>,
    private bulkModalService: BulkModalService,
    private featureFlagsService: FeatureFlagsService,
  ) {
  }


  ngOnInit(): void {
    this.devicesSub = this.store.select('devices').pipe(
      map((state) => {
        return state.devices.filter(d => d.Type === 'modbus');
      }),
    ).subscribe(
      (devices) => {
        this.devices = JSON.parse(JSON.stringify(devices)) as Device<DeviceType.Modbus>[];
        this.onDeviceChange();
      },
      () => {
        this.shadeTools.errorDialog();
      },
    );

    this.devicesService.fetchUserDevices$().subscribe();

    if (this.preselectedDevice) {
      this.selectedDevice = this.preselectedDevice;
    }
  }


  ngOnDestroy() {
    this.devicesSub?.unsubscribe();
  }


  onSave() {
    this.editingPoint = undefined;
  }


  onTestRead(configId: ModbusPointID) {
    if (!configId) {
      return;
    }
    this.performTestRead(configId);
  }


  onTestWrite(configId: ModbusPointID, booleanValue?: boolean) {
    if (!configId) {
      return;
    }
    this.performTestWrite(configId, booleanValue);
  }


  private performTestRead(configId: ModbusPointID) {
    if (this.reading) {
      return;
    }

    this.reading = true;

    this.devicesService.readPointValue$(configId.serialize(), this.selectedDevice).subscribe(
      (data) => {
        if (data.length) {
          this.shadeTools.simpleOkDialog('Success', `The following value was returned: ${data[0].Value}`);
          this.reading = false;
        } else {
          this.shadeTools.simpleOkDialog('Failure', 'No value was able to be read with the given parameters.');
          this.reading = false;
        }
      },
      () => {
        this.shadeTools.simpleOkDialog('Failure', 'An error occurred while trying to read the point.');
        this.reading = false;
      },
    );
  }


  private performTestWrite(configId: ModbusPointID, booleanValue?: boolean) {
    // For 1-bit types, use the provided boolean value directly
    if (is1BitDataType(configId.Type) && booleanValue !== undefined) {
      this.writing = true;

      const displayValue = booleanValue ? 'TRUE' : 'FALSE';

      const pv: PointValue = {
        PointID: configId.serialize(),
        MotionDeviceID: this.selectedDevice,
        Value: booleanValue,
      };

      this.shadeTools.startDialog(
        'Write Value Test',
        `You are about to write the value <strong>${displayValue}</strong> to the device.\n\nCAUTION: The value you send will actually be written to the device. Ensure the device is not performing any critical functions before proceeding.`,
        [
          {
            text: `Write ${displayValue}`,
            callback: () => {
              this.devicesService.writePointValue$([pv]).subscribe(
                (data) => {
                  this.shadeTools.simpleOkDialog('No Errors', `No errors were encountered when attempting to write the value. To verify successful writing, perform a test read.`);
                  this.writing = false;
                },
                () => {
                  this.shadeTools.simpleOkDialog('Failure', 'No value was able to be written with the given parameters.');
                  this.writing = false;
                },
              );
            },
          },
          {
            text: 'Cancel',
            callback: () => {
              this.shadeTools.closeAll();
              this.writing = false;
            },
          },
        ],
        false,
        false,
        true,
        false,
      );
    } else {
      // For non-1-bit types, show input dialog
      this.shadeTools.startDialog(
        'Write Value Test',
        'Enter a numeric value to attempt to write.\n\nCAUTION: The value you send will actually be written to the device. Ensure the device is not performing any critical functions before proceeding.',
        [
          {
            text: 'Write Value',
            callback: (input) => {
              this.writing = true;

              const pv: PointValue = {
                PointID: configId.serialize(),
                MotionDeviceID: this.selectedDevice,
                Value: +input,
              };

              this.devicesService.writePointValue$([pv]).subscribe(
                (data) => {
                  this.shadeTools.simpleOkDialog('No Errors', `No errors were encountered when attempting to write the value. To verify successful writing, perform a test read.`);
                  this.writing = false;
                },
                () => {
                  this.shadeTools.simpleOkDialog('Failure', 'No value was able to be written with the given parameters.');
                  this.writing = false;
                },
              );
            },
          },
          {
            text: 'Cancel',
            callback: () => {
              this.shadeTools.closeAll();
            },
          },
        ],
        true,
        false,
        false,
        false,
      );
    }
  }


  addPoint() {
    this.editingPoint = {
      ID: '',
      Name: '',
    };
  }


  getCurrentSelectedDevice(): Device {
    return this.devices.find(d => d.ID === this.selectedDevice);
  }


  /**
   *
   */
  onDeviceChange(): void {
    this.points = this.getPoints();
    this.filterAndRenderPoints();
  }


  private getPoints(): ModbusPoint[] {
    const device = this.getCurrentSelectedDevice();
    return device?.Config?.Modbus?.Points || [];
  }


  onDeviceMenuClick() {
    if (this.selectionMode) return;
    const options: Option[] = [];

    for (let device of this.devices) {
      options.push({
        id: '' + device.ID,
        name: device.Name,
        selected: this.selectedDevice === device.ID,
        callback: () => {
          this.selectedDevice = device.ID;
        },
        pill: {
          letter: 'd',
          text: '' + device.ID,
        },
      });
    }

    this.deviceMenuOptions = new OptionGroupMultiSelect(options);
  }


  onDeviceMenuDone() {
    this.deviceMenuOptions = undefined;
  }


  /**
   *
   */
  infoDevicesMenu(): void {
    this.shadeTools.startDialog(
      `<i class="fa-light fa-face-monocle"></i> Don't See Your Device?`,
      `Only the Modbus TCP devices you've already added or which another user has shared with you appear here.`,
      [
        {
          text: 'OK',
          callback: () => {
          },
          color: DialogBoxButtonColor.Default,
        },
      ],
    );
  }


  /**
   *
   */
  filterAndRenderPoints(): void {
    let filteredPoints: ModbusPoint[];

    if (this.searchTerm.trim() === '') {
      filteredPoints = [...this.points];
    } else {
      const searchTerm = this.searchTerm.toLowerCase();

      filteredPoints = this.points.filter((point) => {
        return point.Name.toLowerCase().indexOf(searchTerm) > -1;
      });
    }

    // Sort by Modbus address (register number) for logical ordering
    filteredPoints = filteredPoints.sort((a, b) => {
      const configA = new ModbusPointID(a.ID);
      const configB = new ModbusPointID(b.ID);
      return configA.Address - configB.Address;
    });

    this.filteredPoints = filteredPoints;
    this.updateTable();
  }


  private updateTable() {
    const colKebab = 40;
    const colName = 250;
    const colButton = 120;
    const colAddr = 120;
    const colType = 130;
    const colByteOrder = 130;
    const colFuncs = 120;

    const table: SortTable = {
      css: {
        'min-width': (colKebab + colName + colButton + colAddr + colType + colByteOrder + colFuncs) + 'px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 50,
        startOnPage: 1,
        maxPageOptions: 12,
      },
    };

    {
      table.columns[COL_IDX_KEBAB] = {
        name: '',
        css: {
          'width': colKebab + 'px',
        },
      };

      table.columns[COL_IDX_NAME] = {
        name: 'Point Name',
        css: {
          'width': `calc(100% - ${colKebab + colButton + colAddr + colType + colByteOrder + colFuncs}px)`,
        },
      };

      table.columns[COL_IDX_BUTTON] = {
        name: '',
        css: {
          'width': colButton + 'px',
        },
      };

      table.columns[COL_IDX_ADDR] = {
        name: 'Address',
        css: {
          'width': colAddr + 'px',
          'justify-content': 'center',
          'text-align': 'center',
        },
      };

      table.columns[COL_IDX_TYPE] = {
        name: 'Data Type',
        css: {
          'width': colType + 'px',
          'justify-content': 'center',
          'text-align': 'center',
        },
      };

      table.columns[COL_IDX_BYTE_ORDER] = {
        name: 'Byte Order',
        css: {
          'width': colByteOrder + 'px',
          'justify-content': 'center',
          'text-align': 'center',
        },
      };

      table.columns[COL_IDX_FUNCS] = {
        name: 'Functions',
        css: {
          'width': colFuncs + 'px',
          'justify-content': 'center',
          'text-align': 'center',
        },
      };
    }

    for (let point of this.filteredPoints) {
      const row: SortTableRow = {
        cells: [],
        context: point.ID + point.Name,
      };

      this.upsertRowData(row, point);
      table.rows.push(row);
    }

    this.table$.next(table);
  }


  private upsertRowData(row: SortTableRow, point: ModbusPoint) {
    const pointId: ModbusPointID = new ModbusPointID(point.ID);
    const kebabOptions: Option[] = [
      {
        id: 'tr',
        name: `Test Read`,
        callback: () => {
          this.performTestRead(pointId);
        },
      },
      {
        id: 'cp',
        name: `Configure Point`,
        callback: () => {
          this.editingPoint = point;
        },
      },
      {
        id: 'dp',
        name: `Delete Point`,
        callback: () => {
          this.shadeTools.startDialog(
            'Delete Modbus Point',
            'Are you sure you want to delete this point?',
            [
              {
                text: 'Yes, Delete',
                callback: () => {
                  this.devicesService.deleteModbusPoint$(pointId, this.selectedDevice).subscribe(
                    () => {
                      const device = this.getCurrentSelectedDevice();

                      device.Config.Modbus.Points = device.Config.Modbus.Points.filter(
                        (p) => {
                          return p.ID !== pointId.serialize();
                        },
                      );

                      this.onDeviceChange();
                      this.shadeTools.closeAll();
                    },
                    () => {
                      this.shadeTools.errorDialog();
                    },
                  );
                },
                color: DialogBoxButtonColor.Red,
              },
              {
                text: 'Cancel',
                callback: () => {
                },
                color: DialogBoxButtonColor.Alt,
              },
            ],
            false,
            false,
            false,
            false,
          );
        },
      },
    ];

    if (pointId.Writable) {
      if (is1BitDataType(pointId.Type)) {
        // For 1-bit types, show "Test Write FALSE" and "Test Write TRUE"
        kebabOptions.splice(1, 0, {
            id: 'tw0',
            name: `Test Write FALSE`,
            callback: () => {
              this.performTestWrite(pointId, false);
            },
          },
          {
            id: 'tw1',
            name: `Test Write TRUE`,
            callback: () => {
              this.performTestWrite(pointId, true);
            },
          },
        );
      } else {
        // For non-1-bit types, show normal "Test Write"
        kebabOptions.splice(1, 0, {
            id: 'tw',
            name: `Test Write`,
            callback: () => {
              this.performTestWrite(pointId);
            },
          },
        );
      }
    }

    let funcCodesText;
    if (pointId.Writable) {
      funcCodesText = makeFriendlyFunctionsString(getReadWriteFunctionsForDataType(pointId.Type));
    } else {
      funcCodesText = makeFriendlyFunctionsString(getReadOnlyFunctionsForDataType(pointId.Type));
    }


    const makeTileOptions: Option[] = [];
    const makeTileCallback: ModbusPointSelectionCallback = this.customCallback ? this.customCallback : this.createTileCallback.bind(this);

    if (this.formatRestriction) {
      const newTileType = tileFormatToTileTypeMap[this.formatRestriction];

      makeTileOptions.push({
          id: 'numeric',
          name: 'Create tile',
          callback: () => {
            makeTileCallback(point, newTileType, this.selectedDevice);
          },
        },
      );
    } else {
      if (pointId.Type === ModbusDataType.Bool) {
        makeTileOptions.push(
          {
            id: 'toggle',
            name: 'Create toggle tile',
            callback: () => {
              makeTileCallback(point, TILE_TYPE_SIMPLE_BOOL, this.selectedDevice);
            },
          },
        );
      } else {
        makeTileOptions.push(
          {
            id: 'numeric',
            name: 'Create numeric tile',
            callback: () => {
              makeTileCallback(point, TILE_TYPE_SIMPLE_ANALOG, this.selectedDevice);
            },
          },
          {
            id: 'multichoice',
            name: 'Create multi-choice tile',
            callback: () => {
              makeTileCallback(point, TILE_TYPE_SIMPLE_MULTISTATE, this.selectedDevice);
            },
          },
        );
      }
    }


    if (this.selectionMode && this.customCallback) {
      row.cells[COL_IDX_KEBAB] = new SortTableCellTextPill('', '');
      row.cells[COL_IDX_NAME] = new SortTableCellTitleDescription(point.Name, '');
      row.cells[COL_IDX_BUTTON] = new SortTableCellButtons([
        {
          id: point.ID,
          text: 'Select',
          callback: () => this.customCallback(point, TILE_TYPE_SIMPLE_ANALOG, this.selectedDevice),
          colors: ['indigo'],
        },
      ]);
    } else {
      // Max 5 options: Test Read, Test Write 0, Test Write 1 (if writable 1-bit), Configure Point, Delete Point
      row.cells[COL_IDX_KEBAB] = new SortTableCellKebabMenu(point.ID, kebabOptions, point.Name, false, 5);
      row.cells[COL_IDX_NAME] = new SortTableCellTitleDescription(point.Name, '');
      row.cells[COL_IDX_BUTTON] = new SortTableCellButtons([
        {
          id: point.ID,
          text: 'Make Tile',
          menu: new OptionGroup(makeTileOptions),
          colors: ['indigo'],
        },
      ]);
    }
    row.cells[COL_IDX_ADDR] = new SortTableCellTextPill('' + pointId.Address, '', true);
    row.cells[COL_IDX_TYPE] = new SortTableCellTextPill(getFriendlyTypeString(pointId.Type, pointId.Writable), '');
    row.cells[COL_IDX_BYTE_ORDER] = new SortTableCellTextPill(getFriendlyEndiannessString(pointId.Endianness), '');
    row.cells[COL_IDX_FUNCS] = new SortTableCellTextPill(funcCodesText, '');
  }


  /**
   * @param point
   * @param tileType
   * @param device
   * @private
   * @see customCallback
   */
  private createTileCallback(point: ModbusPoint, tileType: TileType, device: DeviceId) {
    const tile = makeTileFromModbusPoint(point, tileType, device);

    this.tilesService.createTile$(tile).subscribe({
      next: (tileId) => {
        this.editingTile = tileId;
      },
      error: (err) => {
        const message = err.status === 403
          ? 'You do not have permission to create tiles on this hub or device.'
          : 'Failed to create tile. Please try again.';
        this.shadeTools.errorDialog(message);
      },
    });
  }


  hasToolsOptions(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_MODBUS_BULK_CREATION);
  }


  createToolsOptions = (): OptionGroup => {
    const options: Option[] = [];

    // Only show bulk tile creation if feature flag is enabled
    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_MODBUS_BULK_CREATION)) {
      options.push({
        id: 'bulk-creation',
        name: 'Bulk Tile Creation',
        icon: 'layer-group',
        callback: () => this.openBulkTileCreation(),
      });
    }

    return new OptionGroup(options);
  };


  openBulkTileCreation(): void {
    const deviceId = this.selectedDevice;
    const points = this.points; // Pass ModbusPoints directly

    // Open the bulk creation modal using the new service
    this.bulkModalService.openCreationModal({
      deviceId,
      points,
      protocol: DeviceType.Modbus,
    });
  }


  isMobile(): boolean {
    return isMobileDevice();
  }
}
